import React from 'react';
import {
  IndicatorSectionKey,
  stageIdToString,
  IndicatorByService,
  indicatorSectionsInOrder,
  IndicatorWithUpdate,
} from '../../../../ReportBuilder/Components/ImpactIndicator/utils';
import {
  isDirectServiceOrEstimateSelectionNotValid,
} from '../../../../ReportBuilder/Components/IndicatorDetails/IndicatorDetailQuestions/IndicatorFileUpload';
import {
  IndicatorDetails,
  SectionIndicators,
} from '../../../../ReportBuilder/Components/IndicatorDetails/utils';
import { HandleDeleteIndicatorData } from './ImpactTableStage';
import { PropertyData } from 'shared/providers/IndicatorProvider/utils';

const getValidationErrorText = (
  sectionKey: IndicatorSectionKey,
  index: number,
  message: string | JSX.Element,
): string | JSX.Element => {
  switch (index) {
    case 0:
      return (
        <>
          {stageIdToString(sectionKey)}: <strong>1st</strong> indicator is
          missing {message}
        </>
      );
    case 1:
      return (
        <>
          {stageIdToString(sectionKey)}: <strong>2nd</strong> indicator is
          missing {message}
        </>
      );
    case 2:
      return (
        <>
          {stageIdToString(sectionKey)}: <strong>3rd</strong> indicator is
          missing {message}
        </>
      );

    default:
      return (
        <>
          {stageIdToString(sectionKey)}: <strong>{index + 1}th</strong>{' '}
          indicator is missing {message}
        </>
      );
  }
};

export interface ValidateModelErrors {
  sectionKey: IndicatorSectionKey;
  index?: number;
  indicationId?: number;
  plainText: string;
  errorMessage: string | JSX.Element;
}

export type HandleUpdatePropertyData =
  | ({
      type: 'impactSummary';
    } & PropertyData<IndicatorWithUpdate> &
      HandleDeleteIndicatorData)
  | ({
      type: 'impactDetail';
    } & PropertyData<IndicatorDetails> &
      HandleDeleteIndicatorData);

export interface ModelSaveWarning {
  index: number;
  indicationId: number;
  sectionKey: IndicatorSectionKey;
  propertyName: HandleUpdatePropertyData['name'];
}

export interface ErrorsByIndicatorId {
  [indicatorId: number]: {
    [key in keyof (IndicatorDetails & {
      sourcesAndAssumptions: string;
    } & Pick<IndicatorWithUpdate, 'updatedText'>)]?: boolean;
  };
}

export const validateModel = (
  summary: IndicatorByService,
  details: SectionIndicators,
  isReportPassed: boolean,
): {
  errors: ValidateModelErrors[];
  errorsByIndicator: ErrorsByIndicatorId;
} => {
  const errors: ValidateModelErrors[] = [];
  const errorsByIndicator: ErrorsByIndicatorId = {};

  for (let i = 0; i < indicatorSectionsInOrder.length; i++) {
    const sectionKey = indicatorSectionsInOrder[i];
    if (sectionKey === 'catalyzed') continue;
    for (let j = 0; j < summary[sectionKey].length; j++) {
      const impactIndicator = summary[sectionKey][j];
      const detailIndicator = details[sectionKey][j] || {};
      const indicationId = impactIndicator.id;
      const isNotValid = isDirectServiceOrEstimateSelectionNotValid(
        sectionKey,
        detailIndicator,
      );
      errorsByIndicator[indicationId] = {};
      if (!impactIndicator.updatedText) {
        errorsByIndicator[indicationId].updatedText = true;
        errors.push({
          sectionKey,
          indicationId,
          index: j,
          plainText: 'missing definition',
          errorMessage: getValidationErrorText(sectionKey, j, 'definition.'),
        });
      }
      if (sectionKey === 'succeed' && !detailIndicator.timePeriod) {
        errorsByIndicator[indicationId].timePeriod = true;
        errors.push({
          sectionKey,
          index: j,
          indicationId,
          plainText: 'missing time period',
          errorMessage: getValidationErrorText(sectionKey, j, 'time period.'),
        });
      }
      if (!detailIndicator.measurement) {
        errorsByIndicator[indicationId].measurement = true;
        errors.push({
          sectionKey,
          index: j,
          indicationId,
          plainText: 'missing measurement type',
          errorMessage: getValidationErrorText(
            sectionKey,
            j,
            'measurement type.',
          ),
        });
      }
      if (isNotValid) {
        errorsByIndicator[indicationId].sourcesAndAssumptions = true;
        errors.push({
          sectionKey,
          index: j,
          indicationId,
          plainText: 'missing sources and assumptions',
          errorMessage: getValidationErrorText(
            sectionKey,
            j,
            'sources and assumptions.',
          ),
        });
      }
      if (!isReportPassed && !detailIndicator.forecast) {
        errorsByIndicator[indicationId].forecast = true;
        errors.push({
          sectionKey,
          index: j,
          indicationId,
          plainText: 'missing forecast',
          errorMessage: getValidationErrorText(sectionKey, j, 'forecast.'),
        });
      }
      if (isReportPassed && !detailIndicator.finalForecast) {
        errorsByIndicator[indicationId].finalForecast = true;
        errors.push({
          sectionKey,
          index: j,
          indicationId,
          plainText: 'missing final forecast',
          errorMessage: getValidationErrorText(sectionKey, j, 'final results.'),
        });
      }
    }
  }

  if (summary.reach.length === 0) {
    errors.push({
      sectionKey: 'reach',
      plainText: 'Reach should have at least one indicator.',
      errorMessage: 'Reach should have at least one indicator.',
    });
  }
  if (summary.succeed.length === 0) {
    errors.push({
      sectionKey: 'succeed',
      plainText: 'Succeed should have at least one indicator.',
      errorMessage: 'Succeed should have at least one indicator.',
    });
  }

  return { errors, errorsByIndicator };
};
